import { AnimatePresence, motion } from "framer-motion";
import { FaBars, FaTimes } from "react-icons/fa";
import React, { useState } from "react";

import { ButtonLink } from "@/components/ui/Button/Button";
import Link from "next/link";
import Logo from "@/components/ui/Logo";
import clsx from "clsx";
import styles from "./MobileHeader.module.css";
import { useLocale } from "next-intl";
import { useTranslations } from "next-intl";
import { usePathname } from "next/navigation";

type HeaderProps = React.ComponentProps<"header"> & {
  theme: "light" | "dark";
};

const MobileHeader = ({ className, ...delegated }: HeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const locale = useLocale();
  const pathname = usePathname();
  const t = useTranslations("header");

  const otherLocale = locale === "en" ? "ar" : "en";
  const switchLabel = locale === "en" ? "عربي" : "English";

  // Extract the path without the locale to keep the same page when switching languages
  const pathnameWithoutLocale = pathname.replace(`/${locale}`, '');
  // Use the current path or root if we're already at root
  const targetPath = pathnameWithoutLocale || '/';

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const menuVariants = {
    hidden: { x: "-100%", opacity: 0 },
    visible: { x: 0, opacity: 1 },
    exit: { x: "-100%", opacity: 0 },
  };

  const linkVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.4, duration: 0.4 },
    }),
    exit: { opacity: 0, y: -10 },
  };

  return (
    <header className={clsx(styles.wrapper, className)} {...delegated}>
      <Logo isFooter={false} />

      <motion.button
        className={styles.burger}
        onClick={toggleMenu}
        whileTap={{ scale: 0.9 }}
        aria-label="Toggle Menu"
      >
        {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.nav
            className={styles.nav}
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={menuVariants}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
          >
            <motion.div
              className={styles.linkContainer}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {[
                { name: "boursaApp", link: "mobile-app" },
                { name: "competition", link: "competition" },
                { name: "about", link: "about-us" },
                { name: "blog", link: "blog" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  custom={index}
                  variants={linkVariants}
                  onClick={toggleMenu}
                >
                  <Link href={`/${locale}/${item.link}`} locale={false}>
                    {t(item.name)}
                  </Link>
                </motion.div>
              ))}
            </motion.div>
            <div className={styles.actions}>
              <ButtonLink
                href="https://store.boursa.io/5O4u/web"
                isExternal
                className={styles.action}
                onClick={toggleMenu}
              >
                {t("downloadApp")}
              </ButtonLink>

              <ButtonLink
                href={`/${otherLocale}${targetPath}`}
                className={styles.action}
                variant="primaryLink"
                onClick={toggleMenu}
              >
                {switchLabel}
              </ButtonLink>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default MobileHeader;
